import { useMutation, useQuery } from '@tanstack/react-query'
import { publicApi, getStoredAuth } from './client'
import { executeRecaptcha } from '../lib/recaptcha'
import type {
  AvailabilityResult,
  Customer,
  CustomerReservation,
  HotelProfile,
  PublicCheckInCompanion,
  PublicCheckInExtractResponse,
  PublicCheckInPhoto,
  PublicCheckInPrimary,
  PublicCheckInSession,
  PublicCheckInSubmitResponse,
  PublicGuestIntakePhoto,
  PublicGuestIntakeSession,
  PublicGuestIntakeSubmitPayload,
  PublicGuestIntakeSubmitResponse,
  ReservationSummary,
  SearchState,
} from './types'

export function useHotelProfile() {
  return useQuery({
    queryKey: ['public-hotel-profile'],
    queryFn: async () => {
      const response = await publicApi.get<HotelProfile>('/public/hotel-profile/')
      return response.data
    },
  })
}

export function useAvailability() {
  return useMutation({
    mutationFn: async (params: SearchState) => {
      const response = await publicApi.get<AvailabilityResult[]>('/public/availability/', { params })
      return response.data
    },
  })
}

export function useAvailabilityQuery(params: SearchState | null) {
  return useQuery({
    queryKey: ['public-availability', params],
    queryFn: async () => {
      const response = await publicApi.get<AvailabilityResult[]>('/public/availability/', {
        params: params!,
      })
      return response.data
    },
    enabled: Boolean(params && params.check_in && params.check_out),
    staleTime: 60_000,
  })
}

export function useCreateReservation() {
  return useMutation({
    mutationFn: async (payload: {
      room_type_id: number
      check_in_date: string
      check_out_date: string
      adults: number
      children: number
      account_mode: string
      special_requests: string
      guest: { first_name: string; last_name: string; email: string; phone: string }
    }) => {
      const auth = getStoredAuth()
      const recaptchaToken = await executeRecaptcha('reserve_room')
      const headers: Record<string, string> = {}
      if (auth) headers.Authorization = `Bearer ${auth.token}`
      if (recaptchaToken) headers['X-Recaptcha-Token'] = recaptchaToken
      const response = await publicApi.post<ReservationSummary>(
        '/public/reservations/',
        payload,
        Object.keys(headers).length ? { headers } : undefined,
      )
      return response.data
    },
  })
}

export function useCustomerLogin() {
  return useMutation({
    mutationFn: async (data: { email: string; password: string }) => {
      const response = await publicApi.post<{ token: string; customer: Customer }>('/public/auth/login/', data)
      return response.data
    },
  })
}

export function useCustomerRegister() {
  return useMutation({
    mutationFn: async (data: {
      first_name: string
      last_name: string
      email: string
      phone: string
      password: string
    }) => {
      const response = await publicApi.post<{ token: string; customer: Customer }>('/public/auth/register/', data)
      return response.data
    },
  })
}

export function useCustomerReservations(customerId: number | undefined) {
  return useQuery({
    queryKey: ['customer-reservations', customerId],
    queryFn: async () => {
      const token = getStoredAuth()?.token
      const response = await publicApi.get<CustomerReservation[]>('/public/me/reservations/', {
        headers: { Authorization: `Bearer ${token}` },
      })
      return response.data
    },
    enabled: Boolean(customerId),
  })
}

export function usePublicCheckInSession(token: string | undefined) {
  return useQuery({
    queryKey: ['public-check-in', token],
    queryFn: async () => {
      const response = await publicApi.get<PublicCheckInSession>(
        `/public/check-in/${token}/`,
      )
      return response.data
    },
    enabled: Boolean(token),
  })
}

export function useUploadPublicCheckInPhoto(token: string | undefined) {
  return useMutation({
    mutationFn: async ({
      file,
      person_type,
      person_key,
    }: {
      file: File
      person_type: 'primary' | 'companion'
      person_key: string
    }) => {
      const formData = new FormData()
      formData.append('person_type', person_type)
      formData.append('person_key', person_key)
      formData.append('image', file)
      const response = await publicApi.post<PublicCheckInPhoto>(
        `/public/check-in/${token}/photos/`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } },
      )
      return response.data
    },
  })
}

export function useExtractPublicCheckInPhoto(token: string | undefined) {
  return useMutation({
    mutationFn: async ({ photoId }: { photoId: number }) => {
      const response = await publicApi.post<PublicCheckInExtractResponse>(
        `/public/check-in/${token}/photos/${photoId}/extract/`,
        {},
      )
      return response.data
    },
  })
}

export function useSubmitPublicCheckIn(token: string | undefined) {
  return useMutation({
    mutationFn: async (payload: {
      primary: PublicCheckInPrimary
      companions: PublicCheckInCompanion[]
      accepted_terms: boolean
    }) => {
      const response = await publicApi.post<PublicCheckInSubmitResponse>(
        `/public/check-in/${token}/submit/`,
        payload,
      )
      return response.data
    },
  })
}

export function usePublicGuestIntakeSession(token: string | undefined) {
  return useQuery({
    queryKey: ['public-guest-intake', token],
    queryFn: async () => {
      const response = await publicApi.get<PublicGuestIntakeSession>(
        `/public/guest-intake/${token}/`,
      )
      return response.data
    },
    enabled: Boolean(token),
  })
}

export function useUploadPublicGuestIntakePhoto(token: string | undefined) {
  return useMutation({
    mutationFn: async ({ file }: { file: File }) => {
      const formData = new FormData()
      formData.append('image', file)
      const response = await publicApi.post<PublicGuestIntakePhoto>(
        `/public/guest-intake/${token}/photos/`,
        formData,
        { headers: { 'Content-Type': undefined } },
      )
      return response.data
    },
  })
}

export function useExtractPublicGuestIntakePhoto(token: string | undefined) {
  return useMutation({
    mutationFn: async ({ photoId }: { photoId: number }) => {
      const response = await publicApi.post<PublicCheckInExtractResponse>(
        `/public/guest-intake/${token}/photos/${photoId}/extract/`,
        {},
      )
      return response.data
    },
  })
}

export function useSubmitPublicGuestIntake(token: string | undefined) {
  return useMutation({
    mutationFn: async (payload: PublicGuestIntakeSubmitPayload) => {
      const response = await publicApi.post<PublicGuestIntakeSubmitResponse>(
        `/public/guest-intake/${token}/submit/`,
        payload,
      )
      return response.data
    },
  })
}
